import {CommentsCategory} from '@domain/comments'
import type {CommentDto} from '@domain/comments'
import type {ScheduleItem} from '@domain/schedule'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {Skeleton} from '@mui/material'
import {CommentInput, useCommentsPermissions} from '@widgets/planning-comments'
import {useCurrentTimeRounded} from '@widgets/stock-card'
import moment from 'moment-timezone'
import {FC, useMemo} from 'react'
import {useForm} from 'react-hook-form'

import {useAutoSaveValue} from '../../../hooks/useAutoSaveValue'

import type {
  AddScheduleCommentParams,
  DeleteCommentParams,
  UpdateScheduleCommentParams
} from './EditScheduleItemForm'

interface ScheduleItemCommentSectionProps {
  comment: CommentDto | undefined
  isCommentsLoading?: boolean
  scheduleItem: ScheduleItem
  timezoneId: string
  addScheduleComment?: (
    params: AddScheduleCommentParams,
    options?: {onSuccess?: () => void}
  ) => void
  isAddCommentLoading?: boolean
  updateScheduleComment?: (params: UpdateScheduleCommentParams) => void
  deleteComment?: (
    params: DeleteCommentParams,
    options?: {onSuccess?: () => void; onError?: () => void}
  ) => void
  isDeletingComment?: boolean
}

export const ScheduleItemCommentSection: FC<ScheduleItemCommentSectionProps> = ({
  comment,
  isCommentsLoading = false,
  timezoneId,
  scheduleItem,
  addScheduleComment,
  isAddCommentLoading = false,
  updateScheduleComment,
  deleteComment,
  isDeletingComment = false
}) => {
  const nowRounded = useCurrentTimeRounded({timezoneId, roundingFn: roundTo15MinIntervalStart})
  const {canEditComments, canAddComments, canDeleteComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )

  const commentInitialState: Pick<CommentDto, 'value'> = {value: comment?.value ?? ''}
  const {
    register,
    formState: {isDirty},
    reset,
    handleSubmit,
    watch
  } = useForm<typeof commentInitialState>({
    defaultValues: commentInitialState,
    values: commentInitialState,
    resetOptions: {keepDirtyValues: true}
  })

  const commentId = comment?.commentId

  const onSubmit = useMemo(
    () =>
      handleSubmit((data: typeof commentInitialState) => {
        if (commentId) {
          return updateScheduleComment?.({
            commentCategoryInstanceId: scheduleItem.id,
            commentId,
            value: data.value
          })
        }
        return addScheduleComment?.(
          {
            commentCategoryInstanceId: scheduleItem.id,
            value: data.value
          },
          {
            onSuccess: () => {
              reset({value: data.value})
            }
          }
        )
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSubmit, addScheduleComment, scheduleItem.id, updateScheduleComment, commentId, reset]
  )

  const handleDeleteComment = commentId
    ? () => {
        deleteComment?.(
          {
            commentCategoryInstanceId: scheduleItem.id,
            commentId
          },
          {
            onError: () => {
              reset(undefined, {keepValues: true})
            },
            onSuccess: () => {
              reset({value: ''})
            }
          }
        )
      }
    : undefined

  // we need to watch this fields to rerun the autosave effect
  const currentCommentValue = watch('value')

  useAutoSaveValue({
    currentValue: currentCommentValue,
    shouldSave: isDirty && currentCommentValue !== '',
    saveFn: onSubmit
  })

  const isHistoryItem = moment.utc(scheduleItem.end).isBefore(nowRounded)

  if (isCommentsLoading) {
    return <Skeleton variant="rectangular" height={168} />
  }

  const isCommentInputDisabled =
    !(canEditComments && canAddComments) ||
    isDeletingComment ||
    isAddCommentLoading ||
    isHistoryItem
  const isDeleteButtonDisabled = !canDeleteComments || isDeletingComment
  return (
    <CommentInput
      testId="schedule_item_comment_input"
      inputProps={{...register('value')}}
      comment={comment}
      currentCommentValue={currentCommentValue}
      timezoneId={timezoneId}
      onDelete={isHistoryItem ? undefined : handleDeleteComment}
      isCommentInputDisabled={isCommentInputDisabled}
      isDeleteButtonDisabled={isDeleteButtonDisabled}
    />
  )
}
